import { z } from 'zod';

import {
  type AnswerReading,
  answerListLength,
  readAnswerDocument,
  refusedShape,
  unfencedAnswer,
} from './answer-document.ts';
import { type AnswerWire, answerWire } from './answer-wire.ts';
import type { DgContent, DgQuestion } from './artifacts.ts';
import { canonicalJson } from './canonical-json.ts';
import { DomainError } from './errors.ts';

/**
 * The queue a guide's coverage is checked on. The API process stores the request and enqueues it;
 * the worker, which is the only process holding a model, answers it.
 */
export const QUEUE_DG_COVERAGE = 'dg.coverage';

/**
 * How often the Guide step re-reads a pending check, and how long it waits before it offers "Try
 * again". Placeholder policy values. A pending check older than the wait is replaced by the next
 * request, so a wait shorter than a slow judge call would buy a second call and discard the first
 * one's verdict.
 */
export const DG_COVERAGE_POLL_MS = 1000;
export const DG_COVERAGE_WAIT_MS = 60_000;

/** The longest reason the judge may give for one research question. A placeholder policy value. */
export const MAX_COVERAGE_REASON_CHARS = 300;

/**
 * What the judge answers for each research question.
 *
 * - `covered` — some question in the guide, as it is worded now, answers it.
 * - `gap` — nothing in the guide answers it, whatever the questions claim to serve.
 * - `uncertain` — a question plausibly answers it, but not clearly.
 */
export const DG_COVERAGE_VERDICTS = ['covered', 'gap', 'uncertain'] as const;
export type DgCoverageVerdictKind = (typeof DG_COVERAGE_VERDICTS)[number];

/**
 * What a check judged: one guide version against one research-question version. A verdict answers
 * for exactly this pair, so a reword, a reshape, a redraft or a change to the research questions
 * each leave it answering for a pair the study no longer has.
 */
export const dgCoverageInputsSchema = z.strictObject({
  dg_artifact_id: z.uuid(),
  dg_version: z.int().positive(),
  rqs_version: z.int().positive(),
});
export type DgCoverageInputs = z.infer<typeof dgCoverageInputsSchema>;

/**
 * One research question's line in a verdict, with the guide questions found to answer it.
 *
 * `key` is what the line was judged on (`coverageJudgedKey`), and `guide_key` the whole guide it was
 * judged against (`coverageGuideKey`). A settle writes both on every line of a verdict for the pair
 * the study still holds, and a line carried into a later check keeps its own; a line without them
 * is never carried.
 */
export const dgCoverageEntrySchema = z.strictObject({
  rq_id: z.uuid(),
  verdict: z.enum(DG_COVERAGE_VERDICTS),
  reason: z.string().max(MAX_COVERAGE_REASON_CHARS),
  question_ids: z.array(z.uuid()),
  key: z.string().optional(),
  guide_key: z.string().optional(),
});
export type DgCoverageEntry = z.infer<typeof dgCoverageEntrySchema>;

/** The whole verdict: one entry per research question, in their order. */
export const dgCoverageVerdictSchema = z.strictObject({
  rqs: z.array(dgCoverageEntrySchema),
});
export type DgCoverageVerdict = z.infer<typeof dgCoverageVerdictSchema>;

export const DG_COVERAGE_STATUSES = ['pending', 'ready', 'unreadable', 'unavailable'] as const;
export type DgCoverageStatus = (typeof DG_COVERAGE_STATUSES)[number];

/**
 * The latest request to check the guide against its research questions.
 *
 * - `pending` — the worker has not answered yet.
 * - `ready` — the worker judged the guide, and `verdict` is what it found.
 * - `unreadable` — the model answered, and its answer was not a verdict on every research question.
 * - `unavailable` — no answer could be had: no model is configured, the day's budget is spent, the
 *   call failed, or the study moved before it was asked.
 *
 * A verdict is held only on a `ready` request. `carried` holds the lines kept from an earlier
 * verdict for research questions whose judged inputs this pair left unchanged
 * (`carriedCoverageEntries`). The judge is asked only about the other research questions, and the
 * settle completes its verdict with these.
 */
export const dgCoverageRequestSchema = z
  .strictObject({
    id: z.uuid(),
    status: z.enum(DG_COVERAGE_STATUSES),
    requested_at: z.iso.datetime(),
    inputs: dgCoverageInputsSchema,
    verdict: dgCoverageVerdictSchema.nullable(),
    carried: z.array(dgCoverageEntrySchema).optional(),
  })
  .refine((request) => (request.status === 'ready') === (request.verdict !== null), {
    message: 'a verdict is held exactly when the request is ready',
  });
export type DgCoverageRequest = z.infer<typeof dgCoverageRequestSchema>;

export const DG_COVERAGE_SCHEMA_VERSION = 2;

/** What `studies.dg_coverage` holds: the latest coverage request and its verdict, or none. */
export const storedDgCoverageSchema = z.strictObject({
  schema_version: z.literal(DG_COVERAGE_SCHEMA_VERSION),
  request: dgCoverageRequestSchema.nullable(),
});
export type StoredDgCoverage = z.infer<typeof storedDgCoverageSchema>;

/**
 * Schema version 1, which wrote no keys. It reads as version 2 with its lines as they were, so its
 * verdict still answers for its pair and carries nothing into a later check.
 */
const storedDgCoverageSchemaV1 = z
  .strictObject({ schema_version: z.literal(1), request: dgCoverageRequestSchema.nullable() })
  .transform(({ request }): StoredDgCoverage => ({
    schema_version: DG_COVERAGE_SCHEMA_VERSION,
    request,
  }));

/**
 * The stored request, or nothing. A shape this build cannot read is treated as no request rather
 * than an error: the study's next request replaces it, and the approval reads it as never checked.
 */
export const parseStoredDgCoverage = (stored: unknown): StoredDgCoverage | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = z.union([storedDgCoverageSchema, storedDgCoverageSchemaV1]).safeParse(stored);

  return parsed.success ? parsed.data : null;
};

/** Whether two checks judge the same guide version against the same research-question version. */
export const sameDgCoverageInputs = (left: DgCoverageInputs, right: DgCoverageInputs): boolean =>
  left.dg_artifact_id === right.dg_artifact_id &&
  left.dg_version === right.dg_version &&
  left.rqs_version === right.rqs_version;

/**
 * Whether the request holds a verdict on the guide and research questions the study has now. Only
 * that verdict can approve the guide: one for an earlier version of either answers a question
 * nobody is asking any more.
 */
export const dgCoverageCurrent = (
  request: DgCoverageRequest | null,
  inputs: DgCoverageInputs | null,
): boolean =>
  request !== null &&
  inputs !== null &&
  request.status === 'ready' &&
  request.verdict !== null &&
  sameDgCoverageInputs(request.inputs, inputs);

/**
 * Whether the verdict on the guide and research questions the study has now names a research
 * question nothing in the guide answers, or one it is not sure the guide answers: the one state,
 * besides a change upstream, over which the step offers "Redraft the guide".
 */
export const dgCoverageUnanswered = (
  request: DgCoverageRequest | null,
  inputs: DgCoverageInputs | null,
): boolean =>
  dgCoverageCurrent(request, inputs) &&
  (request?.verdict?.rqs.some((entry) => entry.verdict !== 'covered') ?? false);

/** A research question a repair answers: its verdict, `gap` or `uncertain`, and the judge's reason. */
export interface DgCoverageUnansweredLine {
  rqId: string;
  verdict: Exclude<DgCoverageVerdictKind, 'covered'>;
  reason: string;
}

/** Each `gap` and `uncertain` line of a verdict, in order, with its verdict and the judge's reason. */
export const dgCoverageUnansweredLines = (verdict: DgCoverageVerdict): DgCoverageUnansweredLine[] =>
  verdict.rqs.flatMap((entry) =>
    entry.verdict === 'covered'
      ? []
      : [{ rqId: entry.rq_id, verdict: entry.verdict, reason: entry.reason }],
  );

/**
 * The research questions a repair answers: each `gap` and `uncertain` line of the verdict, with the
 * judge's reason, when that verdict is `ready` on exactly `inputs` — the guide and research-question
 * versions the repair is asked over. Empty for no verdict, one on another pair and one that found
 * every research question covered.
 */
export const dgCoverageUnansweredOn = (
  request: DgCoverageRequest | null,
  inputs: DgCoverageInputs | null,
): DgCoverageUnansweredLine[] =>
  dgCoverageCurrent(request, inputs) && request !== null && request.verdict !== null
    ? dgCoverageUnansweredLines(request.verdict)
    : [];

/**
 * Whether a study needs a new check for its current guide and research questions, rather than the
 * request it holds: it holds none, the one it holds judged a different pair, or it is pending for
 * this pair and older than `DG_COVERAGE_WAIT_MS` — its job was lost with a worker, expired, or never
 * ran, and since the queue retries nothing, treating it as in hand would leave the step waiting on
 * nothing. A settled request for this pair, whatever it found, is not asked again unasked.
 */
export const dgCoverageRequestNeeded = ({
  request,
  inputs,
  now,
}: {
  request: DgCoverageRequest | null;
  inputs: DgCoverageInputs;
  now: Date;
}): boolean => {
  if (request === null || !sameDgCoverageInputs(request.inputs, inputs)) {
    return true;
  }

  return (
    request.status === 'pending' &&
    now.getTime() - Date.parse(request.requested_at) >= DG_COVERAGE_WAIT_MS
  );
};

/**
 * Whether the step's "Try again" may ask again for the same pair: only after a check that found
 * nothing to report — no answer to be had, or one that was not a verdict. A verdict is not asked
 * again: the guide or its research questions change, and that is a new pair.
 */
export const dgCoverageRetryOffered = (
  request: DgCoverageRequest | null,
  inputs: DgCoverageInputs,
): boolean =>
  request !== null &&
  sameDgCoverageInputs(request.inputs, inputs) &&
  (request.status === 'unreadable' || request.status === 'unavailable');

/** One research question's line in a refusal: which one, and why the judge said so. */
export interface DgCoverageRefusalEntry {
  rq_id: string;
  reason: string;
}

const refusalEntries = (verdict: DgCoverageVerdict, kind: DgCoverageVerdictKind) =>
  verdict.rqs
    .filter((entry) => entry.verdict === kind)
    .map((entry): DgCoverageRefusalEntry => ({ rq_id: entry.rq_id, reason: entry.reason }));

/**
 * Whether a verdict answers for a guide and research questions as they stand: one entry for each
 * research question, in their order, naming only questions the guide holds. What a settle records
 * as `unreadable` when it does not hold, and what the approval reads as never checked.
 */
export const coverageVerdictFits = (
  verdict: DgCoverageVerdict,
  { rqIds, questionIds }: { rqIds: readonly string[]; questionIds: readonly string[] },
): boolean => {
  const held = new Set(questionIds);

  return (
    verdict.rqs.length === rqIds.length &&
    verdict.rqs.every(
      (entry, index) =>
        entry.rq_id === rqIds[index] && entry.question_ids.every((id) => held.has(id)),
    )
  );
};

/**
 * Refuses to approve a guide the stored check does not clear, in order:
 *
 * 1. `dg_coverage_not_checked` — no verdict on the guide and research questions the study has now:
 *    none asked for, still pending, unavailable, unreadable, one for an earlier version of either,
 *    or one that does not answer for the guide and research questions as they stand
 *    (`coverageVerdictFits`).
 * 2. `dg_coverage_gap` — some research question has nothing in the guide answering it. Its reason
 *    is in `detail.gaps`. Nothing the researcher sends clears it; the guide has to change.
 * 3. `dg_coverage_uncertain` — some research question is only plausibly answered, and the
 *    researcher did not say to approve anyway (`acceptUncertain`). Its reason is in
 *    `detail.uncertain`.
 *
 * That uncertain coverage is approvable only when the researcher says so, and a check that could
 * not be made blocks, is placeholder policy.
 */
export const assertDgCoverage = ({
  request,
  inputs,
  rqIds,
  questionIds,
  acceptUncertain,
}: {
  request: DgCoverageRequest | null;
  inputs: DgCoverageInputs | null;
  /** The research questions the study has now, which a verdict must answer for one each. */
  rqIds: readonly string[];
  /** The questions the guide holds now, the only ones a verdict may name. */
  questionIds: readonly string[];
  acceptUncertain: boolean;
}): void => {
  const verdict = dgCoverageCurrent(request, inputs) ? request?.verdict : null;
  if (
    verdict === null ||
    verdict === undefined ||
    !coverageVerdictFits(verdict, { rqIds, questionIds })
  ) {
    throw new DomainError(
      'dg_coverage_not_checked',
      'Sigora has not checked this version of the guide against your research questions yet.',
      {
        status: request?.status ?? null,
        checked: request?.inputs ?? null,
        current: inputs,
      },
    );
  }

  const gaps = refusalEntries(verdict, 'gap');
  if (gaps.length > 0) {
    throw new DomainError(
      'dg_coverage_gap',
      gaps.length === 1
        ? 'Nothing in the guide answers one of your research questions yet.'
        : `Nothing in the guide answers ${String(gaps.length)} of your research questions yet.`,
      { gaps },
    );
  }

  const uncertain = refusalEntries(verdict, 'uncertain');
  if (uncertain.length > 0 && !acceptUncertain) {
    throw new DomainError(
      'dg_coverage_uncertain',
      'Sigora is not sure the guide answers every research question. Approve anyway to go ahead.',
      { uncertain },
    );
  }
};

/** Every question in the guide, in the order participants meet them: what the judge numbers. */
export const guideQuestionsInOrder = (content: DgContent): DgQuestion[] =>
  content.sections.flatMap((section) => section.questions);

/** The file id of every image the guide shows, in the order participants meet them. */
export const shownFileIds = (content: DgContent): string[] =>
  guideQuestionsInOrder(content).flatMap((question) =>
    question.stimulus === undefined ? [] : [question.stimulus.file_id],
  );

/**
 * What one research question's line in a verdict was judged on, as one string: the decision the
 * chosen goal informs, the research question's text and decision, and, in guide order, every
 * question that claims to serve it or that the line names, each as stored but for its id and
 * whose words it is in, its answers by their text, under the title of the section that holds it.
 *
 * Two pairs with the same key for a research question asked the judge the same thing about it, so
 * the line one verdict gave it still answers for the other. Built from content, never from the
 * judge's numbering, which moves whenever an earlier question is added or removed. Any change to a
 * question the line rests on — its words, kind, answers or claims, or the title it is asked under,
 * which the judge reads it beside — changes the key; a change to a question or section it does not
 * rest on leaves it alone.
 */
export const coverageJudgedKey = ({
  goalDecision,
  rq,
  content,
  questionIds = [],
}: {
  goalDecision: string;
  rq: { id: string; text: string; decision: string };
  content: DgContent;
  /** The questions a verdict's line names, which it rests on whatever they claim. */
  questionIds?: readonly string[];
}): string => {
  const named = new Set(questionIds);

  return canonicalJson({
    goal_decision: goalDecision,
    rq: { text: rq.text, decision: rq.decision },
    sections: content.sections.flatMap((section) => {
      const questions = section.questions
        .filter((question) => question.serves_rq_ids.includes(rq.id) || named.has(question.id))
        .map(({ id: _id, worded_by: _wordedBy, options, ...judged }) => ({
          ...judged,
          ...(options === undefined ? {} : { options: options.map((option) => option.text) }),
        }));

      return questions.length === 0 ? [] : [{ title: section.title, questions }];
    }),
  });
};

/**
 * The whole guide one research question's line was judged against, as one string: the decision the
 * chosen goal informs, the research question's text and decision, and every section title and
 * every question in guide order, each as stored but for its id and whose words it is in.
 *
 * What a line other than `covered` needs unchanged to be carried. The judge reads wording rather
 * than claims, so any question — whatever it claims to serve — may answer a research question the
 * last check found a gap in or was unsure of; only a guide nobody has touched since is sure not to.
 * A `covered` line needs only its own `coverageJudgedKey`, since nothing outside the questions it
 * rests on can take its coverage away.
 */
export const coverageGuideKey = ({
  goalDecision,
  rq,
  content,
}: {
  goalDecision: string;
  rq: { text: string; decision: string };
  content: DgContent;
}): string =>
  canonicalJson({
    goal_decision: goalDecision,
    rq: { text: rq.text, decision: rq.decision },
    sections: content.sections.map((section) => ({
      title: section.title,
      questions: section.questions.map(({ id: _id, worded_by: _wordedBy, options, ...judged }) => ({
        ...judged,
        ...(options === undefined ? {} : { options: options.map((option) => option.text) }),
      })),
    })),
  });

/** A research question as a coverage check judges it, and the guide it is judged against. */
export interface CoverageJudgedPair {
  goalDecision: string;
  rqs: readonly { id: string; text: string; decision: string }[];
  content: DgContent;
}

/** Every line of a verdict with the key it was judged on, for the pair it was judged against. */
export const keyedCoverageEntries = (
  entries: readonly DgCoverageEntry[],
  { goalDecision, rqs, content }: CoverageJudgedPair,
): DgCoverageEntry[] =>
  entries.map((entry) => {
    const rq = rqs.find((each) => each.id === entry.rq_id);

    return rq === undefined
      ? entry
      : {
          ...entry,
          key: coverageJudgedKey({ goalDecision, rq, content, questionIds: entry.question_ids }),
          guide_key: coverageGuideKey({ goalDecision, rq, content }),
        };
  });

/**
 * The lines an earlier check gave that still answer for a new pair: one for each research question
 * whose key the new pair leaves unchanged and every question it names is one the guide still holds.
 * Read from a request's verdict where it holds a keyed line for the research question, and
 * otherwise from the lines the request carried itself: so a retry, a check that found nothing, and
 * a verdict settled after its pair moved — which is stored as the judge gave it, unkeyed — lose
 * none of them. A `gap` or `uncertain` line is carried only while the whole guide it was judged
 * against is unchanged too (`coverageGuideKey`), so a guide edit that may answer it always has it
 * judged again. A line with no key is never carried.
 */
export const carriedCoverageEntries = (
  previous: DgCoverageRequest | null,
  pair: CoverageJudgedPair,
): DgCoverageEntry[] => {
  const judged = (previous?.verdict?.rqs ?? []).filter((entry) => entry.key !== undefined);

  return entriesStillAnswering(
    [
      ...judged,
      ...(previous?.carried ?? []).filter(
        (entry) => !judged.some((each) => each.rq_id === entry.rq_id),
      ),
    ],
    pair,
  );
};

/**
 * The keyed lines, among `earlier`, that still answer for `pair`, by the rule `carriedCoverageEntries`
 * applies: one for each research question whose line's key the pair leaves unchanged and every
 * question it names is one the guide still holds, and for a `gap` or `uncertain` line the whole guide
 * unchanged too. For lines held in memory, such as a verdict on a guide not yet written; the first
 * line for a research question is the one weighed.
 */
export const entriesStillAnswering = (
  earlier: readonly DgCoverageEntry[],
  pair: CoverageJudgedPair,
): DgCoverageEntry[] => {
  const held = new Set(guideQuestionsInOrder(pair.content).map((question) => question.id));

  return pair.rqs.flatMap((rq) => {
    const entry = earlier.find((each) => each.rq_id === rq.id);
    if (
      entry?.key === undefined ||
      !entry.question_ids.every((id) => held.has(id)) ||
      entry.key !==
        coverageJudgedKey({
          goalDecision: pair.goalDecision,
          rq,
          content: pair.content,
          questionIds: entry.question_ids,
        }) ||
      (entry.verdict !== 'covered' &&
        entry.guide_key !==
          coverageGuideKey({ goalDecision: pair.goalDecision, rq, content: pair.content }))
    ) {
      return [];
    }

    return [entry];
  });
};

/**
 * The whole verdict for a pair: for each research question in order, the line carried for it, or
 * else the judge's own. The judge is asked only about the research questions nothing was carried
 * for, so a line missing from both leaves the verdict short, which `coverageVerdictFits` refuses.
 */
export const withCarriedEntries = (
  verdict: DgCoverageVerdict,
  carried: readonly DgCoverageEntry[],
  rqIds: readonly string[],
): DgCoverageVerdict => ({
  rqs: rqIds.flatMap((rqId) => {
    const entry =
      carried.find((each) => each.rq_id === rqId) ??
      verdict.rqs.find((each) => each.rq_id === rqId);

    return entry === undefined ? [] : [entry];
  }),
});

/** One research question's entry in the judge's answer format: every key, and no bound. */
const coverageEntryWireShape = z.strictObject({
  rq: z.int(),
  verdict: z.enum(DG_COVERAGE_VERDICTS),
  reason: z.string(),
  questions: z.array(z.int()),
});

/**
 * The judge's answer format's shape, as the provider is asked to hold it (`COVERAGE_ANSWER_WIRE`).
 * The parser holds the answer to one entry per research question and every position to one shown.
 */
export const COVERAGE_ANSWER_WIRE_SHAPE = z.strictObject({ rqs: z.array(coverageEntryWireShape) });

/** The wire a coverage check answers under. */
export const COVERAGE_ANSWER_WIRE: AnswerWire = answerWire(
  'coverage_answer',
  COVERAGE_ANSWER_WIRE_SHAPE,
);

const coverageAnswerSchema = COVERAGE_ANSWER_WIRE_SHAPE.extend({
  rqs: z.array(
    coverageEntryWireShape.extend({
      rq: z.int().positive(),
      reason: z.string().trim(),
      questions: z.array(z.int().positive()),
    }),
  ),
});

/**
 * The rules a judge's answer can break, each the reason `parseCoverageAnswer` refuses it:
 *
 * - `document`: not one JSON document;
 * - `shape`: not the answer format's shape, a position below 1 included;
 * - `count`: not one entry per research question;
 * - `rq`: an entry naming a research question past the last, or one named already;
 * - `question`: an entry naming a guide question past the last, or one twice;
 * - `covered`: a `covered` entry naming no question that answers it;
 * - `reason`: a `gap` or `uncertain` entry that does not say why.
 */
export const COVERAGE_ANSWER_RULES = [
  'document',
  'shape',
  'count',
  'rq',
  'question',
  'covered',
  'reason',
] as const;

export type CoverageAnswerRule = (typeof COVERAGE_ANSWER_RULES)[number];

/**
 * A reason cut to `MAX_COVERAGE_REASON_CHARS`, at the last word boundary that leaves room for an
 * ellipsis. A reason is the judge's explanation rather than its verdict, so one that runs long is
 * shortened rather than costing the whole verdict.
 */
export const boundedCoverageReason = (reason: string): string => {
  if (reason.length <= MAX_COVERAGE_REASON_CHARS) {
    return reason;
  }

  const room = reason.slice(0, MAX_COVERAGE_REASON_CHARS - 1);
  const boundary = room.lastIndexOf(' ');

  return `${(boundary > 0 ? room.slice(0, boundary) : room).trimEnd()}…`;
};

/** The document the judge answers with, as its instructions show it. */
export const COVERAGE_ANSWER_EXAMPLE =
  '{"rqs": [{"rq": <research question number>, "verdict": "covered" | "gap" | "uncertain", "reason": "<why, in one or two sentences>", "questions": [<guide question number>, ...]}, ...]}';

/**
 * The verdict a judge's answer gives, or the rule it broke when it is not a verdict on every
 * research question.
 *
 * The whole answer must be one JSON document, optionally inside one code fence. It must hold exactly
 * one entry per research question, each named once by its position in the list the judge was shown;
 * every guide question it names must be one it was shown, named once; a `covered` research question
 * must name the questions that answer it, and a `gap` or `uncertain` one must say why. Anything short
 * of that is not a verdict, so an unjudged research question can never be read as covered. A reason
 * longer than `MAX_COVERAGE_REASON_CHARS` is shortened (`boundedCoverageReason`), not refused.
 *
 * Positions become ids here, in the order of `rqIds`; the judge writes neither.
 */
export const parseCoverageAnswer = (
  answer: string,
  { rqIds, questionIds }: { rqIds: readonly string[]; questionIds: readonly string[] },
): AnswerReading<DgCoverageVerdict, CoverageAnswerRule> => {
  const read = readAnswerDocument(unfencedAnswer(answer));
  if (read === null) {
    return { ok: false, rule: 'document' };
  }
  const document = read.value;

  const count = answerListLength(document, 'rqs');
  if (count !== undefined && count !== rqIds.length) {
    return { ok: false, rule: 'count', at: 'rqs' };
  }
  const parsed = coverageAnswerSchema.safeParse(document);
  if (!parsed.success) {
    return refusedShape(parsed.error);
  }

  const byRq = new Map(parsed.data.rqs.map((entry, index) => [entry.rq, { entry, index }]));
  if (byRq.size !== rqIds.length) {
    return { ok: false, rule: 'rq', at: 'rqs' };
  }

  const entries: DgCoverageEntry[] = [];
  for (const [position, rqId] of rqIds.entries()) {
    const named = byRq.get(position + 1);
    if (named === undefined) {
      return { ok: false, rule: 'rq', at: 'rqs' };
    }

    const { entry, index } = named;
    const at = `rqs.${String(index)}`;
    const questions = entry.questions;
    if (
      questions.some((question) => question > questionIds.length) ||
      new Set(questions).size !== questions.length
    ) {
      return { ok: false, rule: 'question', at: `${at}.questions` };
    }
    if (entry.verdict === 'covered' && questions.length === 0) {
      return { ok: false, rule: 'covered', at: `${at}.questions` };
    }
    if (entry.verdict !== 'covered' && entry.reason.length === 0) {
      return { ok: false, rule: 'reason', at: `${at}.reason` };
    }

    entries.push({
      rq_id: rqId,
      verdict: entry.verdict,
      reason: boundedCoverageReason(entry.reason),
      question_ids: questions.map((question) => questionIds[question - 1] as string),
    });
  }

  return { ok: true, value: { rqs: entries } };
};

/** How a check is settled: every status but the one it starts in. */
export type DgCoverageOutcome =
  | { status: 'ready'; verdict: DgCoverageVerdict }
  | { status: 'unreadable' }
  | { status: 'unavailable' };

/**
 * The job the API process sends. It names the request as well as the study, so a job for a request
 * a later one has replaced is recognised as stale and spends nothing.
 */
export const dgCoveragePayloadSchema = z.object({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type DgCoveragePayload = z.infer<typeof dgCoveragePayloadSchema>;

/**
 * A deterministic verdict for a stack with no model: `covered` for every research question some
 * question claims to serve, naming those questions, and `gap` for every other. It reads only the
 * claims — what ids join — so it exercises the approval gate and never the judgement; the local
 * fixture route and the runtime checks settle it through the real request and settle.
 */
export const fixtureCoverage = ({
  content,
  rqIds,
}: {
  content: DgContent;
  rqIds: readonly string[];
}): DgCoverageVerdict => {
  const questions = guideQuestionsInOrder(content);

  return {
    rqs: rqIds.map((rqId): DgCoverageEntry => {
      const serving = questions
        .filter((question) => question.serves_rq_ids.includes(rqId))
        .map((question) => question.id);

      return serving.length === 0
        ? {
            rq_id: rqId,
            verdict: 'gap',
            reason: 'No question in the guide says it serves this research question.',
            question_ids: [],
          }
        : { rq_id: rqId, verdict: 'covered', reason: '', question_ids: serving };
    }),
  };
};
